import { forwardRef, useImperativeHandle, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { ManualShopApiAdapter } from '../../adapters/ManualShopApiAdapter';

export interface ManualShopSettingsModalRef {
  open: () => void;
}

interface ManualShopSettingsModalProps {
  onShopCreated?: () => void; // 🎯 Callback para refrescar la lista de tiendas
}

export const ManualShopSettingsModal = forwardRef<ManualShopSettingsModalRef, ManualShopSettingsModalProps>(
  ({ onShopCreated }, ref) => {
    const { t } = useTranslation('shopsModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const [formData, setFormData] = useState({
      shopName: '',
    });
    const [isLoading, setIsLoading] = useState(false);

    // 🎯 Inicializar adapter para tienda manual
    const manualShopAdapter = new ManualShopApiAdapter(createHttpClient(apiClientWH));

    const isFormValid = useMemo(() => {
      return formData.shopName.trim() !== '';
    }, [formData]);

    useImperativeHandle(ref, () => ({
      open: () => {
        setFormData({
          shopName: '',
        });
        modalRef.current?.openModal();
      },
    }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = e.target;
      setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleSave = async () => {
      if (!isFormValid) return;

      setIsLoading(true);

      try {
        // 🎯 Preparar datos para el adapter
        const shopData = {
          name: formData.shopName,
        };

        // 🎯 Llamar al adapter para crear la tienda
        const response = await manualShopAdapter.createManualShop(shopData);

        if (response.success) {
          // 🎯 Mostrar toast de éxito
          toast({
            title: t('modals.manualShop.toast.success.title'),
            description: response.message,
            variant: 'default',
          });

          // 🎯 Cerrar modal y limpiar formulario
          modalRef.current?.closeModal();
          setFormData({
            shopName: '',
          });

          // 🎯 Refrescar la lista de tiendas si es necesario
          onShopCreated?.();
        } else {
          // 🎯 Mostrar toast de error
          toast({
            title: t('modals.manualShop.toast.error.title'),
            description: response.message,
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        // 🎯 Manejar errores inesperados
        toast({
          title: t('modals.manualShop.toast.error.title'),
          description: t('modals.manualShop.toast.error.unexpected'),
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <ModalCenter ref={modalRef} title={t('modals.manualShop.title')}>
        <div className="space-y-6 max-h-[calc(100vh-100px)] overflow-y-auto">
          <Alert className="bg-body-bg mt-2">
            <div className="flex flex-col md:flex-row items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faCircleInfo} className="w-5 h-5" />
              <AlertTitle className="text-lg">{t('modals.manualShop.alert.title')}</AlertTitle>
            </div>

            <AlertDescription className="text-icons text-sm">
              {t('modals.manualShop.alert.description')}
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="shopName">{t('modals.manualShop.labels.shopName')}</Label>
              <Input
                id="shopName"
                value={formData.shopName}
                onChange={handleChange}
                placeholder={t('modals.manualShop.placeholders.shopName')}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="primary" onClick={handleSave} disabled={!isFormValid || isLoading}>
              {isLoading ? t('modals.manualShop.buttons.saving') : t('modals.manualShop.buttons.save')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

ManualShopSettingsModal.displayName = 'ManualShopSettingsModal';
